import type { ProductsCategoryV2, ExcludeIngredient , ProductDetailResponse } from '../model/types'
import { useApi } from '@shared/api'



interface Addon {
  id: number
  name: string
  description?: string
  price: number
  img?: { key?: string; url?: string }
  required?: boolean
  max_quantity?: number
}

export interface ProductsWithAddons {
  categories: ProductsCategoryV2[]
  allAddons: Addon[]
  allExcludeIngredients: ExcludeIngredient[]
}

/** Карточка блюда в конкретном филиале — ответ resolve-for-branch. */
export interface ResolvedBranchProduct {
  /** Запрошенный id (карточка любого филиала) */
  requested: string
  /** Карточка того же блюда в этом филиале; null — блюда здесь нет */
  product_id: string | null
  /** Можно заказать прямо сейчас */
  available: boolean
  /** Временно недоступно (стоп-лист) */
  stop_list: boolean
  /** Цена карточки ЭТОГО филиала */
  price: number | null
  name: string
}

/**
 * API-клиент каталога продуктов.
 * Базовый URL: /api/v2/products
 *
 * SRP: только операции с продуктами.
 * Избранное → apiFavorite.ts | Рекомендации → apiRecommendation.ts
 */
class APIProduct {
  private readonly basePath = '/products'

  /**
   * Получить список продуктов для филиала.
   * GET /api/v2/products/{branchId}
   */
  async getProducts(
    branchId: number,
    searchQuery = '',
    userId?: number,
    tagIds?: number[],
  ): Promise<ProductsWithAddons> {
    const params: Record<string, any> = {
      search: searchQuery || undefined,
      user_id: userId || undefined,
    }

    if (tagIds && tagIds.length > 0) {
      params.tags = tagIds.join(',')
    }

    const response = await useApi(true).get<ProductsWithAddons>(`${this.basePath}/${branchId}`, { params })

    const { categories, allAddons, allExcludeIngredients } = response.data

    if (!Array.isArray(categories)) {
      throw new TypeError('API error: expected categories to be an array')
    }
    if (!Array.isArray(allAddons)) {
      throw new TypeError('API error: expected allAddons to be an array')
    }

    return { categories, allAddons, allExcludeIngredients: allExcludeIngredients ?? [] }
  }

  /**
   * Получить детальную карточку продукта (для модалки + бандлов).
   * GET /api/v2/products/detail/{productId}?branch_id=
   */
  async getProductDetail(productId: number, branchId: number): Promise<ProductDetailResponse> {
    const response = await useApi(true).get<ProductDetailResponse>(`${this.basePath}/detail/${productId}`, {
      params: { branch_id: String(branchId) },
    })
    return response.data
  }

  /**
   * Привести id карточек ЛЮБОГО филиала к карточкам выбранного филиала.
   * POST /api/v2/products/resolve-for-branch
   *
   * Карточка товара принадлежит филиалу (артикул кассы — тоже), поэтому блюдо,
   * заказанное на другой точке, по своему id в текущем меню не находится. Сервер
   * приводит id к карточке филиала через группы «одно блюдо» — этим живут повтор
   * заказа, «вы заказывали» и избранное.
   */
  async resolveForBranch(productIds: Array<string | number>, branchId: number): Promise<ResolvedBranchProduct[]> {
    const ids = [...new Set(productIds.map(String).filter(Boolean))]
    if (ids.length === 0) return []

    const response = await useApi(true).post<{ items: ResolvedBranchProduct[] }>(
      `${this.basePath}/resolve-for-branch`,
      { body: { branch_id: String(branchId), product_ids: ids } },
    )

    return Array.isArray(response.data?.items) ? response.data.items : []
  }
}

export const apiProduct = new APIProduct()
export default apiProduct
